import client from './client';
import {
  CohortPeriod,
  FunnelAnalytics,
  FunnelStage,
  FunnelStageUsers,
  Granularity,
  OtpStats,
  RepeatRateAnalytics,
} from '../types/analytics';

const EMPTY_FUNNEL: FunnelAnalytics['funnel'] = {
  total_signups: 0,
  added_to_cart: 0,
  placed_order: 0,
  repeat_orderers: 0,
};

const EMPTY_RATES: FunnelAnalytics['conversion_rates'] = {
  signup_to_cart: '0.0%',
  cart_to_order: '0.0%',
  order_to_repeat: '0.0%',
};

const EMPTY_FORMULATION_FUNNEL: FunnelAnalytics['formulation_funnel'] = {
  total_formulations_generated: 0,
  added_to_cart: 0,
  placed_order: 0,
  conversion_rate: '0.0%',
  drop_off_at_cart: 0,
  drop_off_at_checkout: 0,
};

export async function getFunnel(params: {
  from: string;
  to: string;
  granularity: Granularity;
}): Promise<FunnelAnalytics> {
  const res = await client.get('/api/admin/analytics/funnel', { params });
  const raw = res.data?.data ?? res.data ?? {};
  // Default every branch so a partial aggregation can never crash the page —
  // the gateway merges three services and any one of them may return nothing.
  return {
    period: raw.period ?? { from: params.from, to: params.to },
    funnel: { ...EMPTY_FUNNEL, ...(raw.funnel ?? {}) },
    formulation_funnel: {
      ...EMPTY_FORMULATION_FUNNEL,
      ...(raw.formulation_funnel ?? {}),
    },
    conversion_rates: { ...EMPTY_RATES, ...(raw.conversion_rates ?? {}) },
    trend: raw.trend ?? [],
  };
}

export async function getFunnelStageUsers(params: {
  from: string;
  to: string;
  stage: FunnelStage;
  page: number;
  limit?: number;
}): Promise<FunnelStageUsers> {
  const res = await client.get('/api/admin/analytics/funnel/users', {
    params: { limit: 20, ...params },
  });
  const raw = res.data?.data ?? res.data ?? {};
  return {
    stage: raw.stage ?? params.stage,
    period: raw.period ?? { from: params.from, to: params.to },
    users: raw.users ?? [],
    pagination: raw.pagination ?? { total: 0, page: 1, limit: 20, pages: 1 },
  };
}

export async function getRepeatRate(params: {
  period: CohortPeriod;
  from: string;
  to: string;
}): Promise<RepeatRateAnalytics> {
  const res = await client.get('/api/admin/analytics/repeat-rate', { params });
  const raw = res.data?.data ?? res.data ?? {};
  return {
    periods: raw.periods ?? [],
    overall_repeat_rate: raw.overall_repeat_rate ?? '0.0%',
  };
}

const EMPTY_OTP_SUMMARY: OtpStats['summary'] = {
  total_sent: 0,
  total_verified: 0,
  total_expired: 0,
  total_failed: 0,
  success_rate: 0,
  unique_phones: 0,
};

/**
 * SMS OTP send/verify health for the last 30 days.
 *
 * Every field is defaulted: the auth service prunes spent OTP rows on a timer,
 * so an empty table is a normal state, not a failure, and must render as zeros
 * rather than crashing the page.
 */
export async function getOtpStats(): Promise<OtpStats> {
  const res = await client.get('/api/admin/auth/otp-stats');
  const raw = res.data?.data ?? res.data ?? {};
  return {
    summary: { ...EMPTY_OTP_SUMMARY, ...(raw.summary ?? {}) },
    daily: Array.isArray(raw.daily) ? raw.daily : [],
  };
}
